import { addZ } from "@jugl-web/utils/date-time/addZ";
import * as yup from "yup";
import { ChatBotMessageDto } from "./ChatBotMessageDto";
import { ChatBotMessageModel } from "./ChatBotMessageModel";

/**
 * Inverts the message perspective so that outgoing messages are considered incoming and vice versa.
 */
const invertMessageType = (
  type: ChatBotMessageModel["type"]
): ChatBotMessageModel["type"] =>
  type === "info" ? "info" : type === "incoming" ? "outgoing" : "incoming";

/**
 * Transforms a ChatBotMessageDto (from socket) to a ChatBotMessageModel
 */
export const transformChatBotMessageDtoToModel = (
  dto: ChatBotMessageDto
): ChatBotMessageModel => {
  return {
    id: dto.msg_id,
    timestamp: new Date(addZ(dto.inserted_at)).getTime(),
    from: dto.from,
    type: invertMessageType(dto.type),
    text: resolveChatMessagePayloadToText(dto.payload),
    quickReplies: dto.quick_replies ?? [],
    autoResponse: dto.auto_response,
    ctaToolResult: dto.cta_tool_result,
  };
};

/**
 * Resolves the text from a chat message payload.
 *
 * If the message is a plain text, it returns the text itself.
 *
 * If the message is a JSON payload, it validates it against
 * the supported schemas and returns the text representation of the payload.
 */
export const resolveChatMessagePayloadToText = <
  TPayload extends { type: "text" | "json"; text: unknown }
>(
  payload: TPayload
): string => {
  if (payload.type === "text") {
    return typeof payload.text === "string" ? payload.text : "";
  }

  // Attempt to parse the JSON
  try {
    const json = JSON.parse(
      typeof payload.text === "string" ? payload.text : ""
    );

    if (submitFormSchema.isValidSync(json)) {
      return (
        Object.values(json.form_data)
          .filter((value) => (value as string).trim().length > 0)
          .join(", ") || "(no data)"
      );
    }

    if (addToCartSchema.isValidSync(json)) {
      return `Add ${json.product.quantity} x ${json.product.name} to cart`;
    }

    throw new Error("Invalid JSON payload");
  } catch {
    return "";
  }
};

const submitFormSchema = yup.object({
  action: yup.string().oneOf(["submit_form"]).required(),
  form_data: yup
    .object()
    .required()
    .test("non-empty", "form_data must have at least one field", (value) => {
      if (!value || typeof value !== "object") return false;
      return Object.keys(value).length > 0;
    })
    .test("all-strings", "All form_data values must be strings", (value) => {
      if (!value || typeof value !== "object") return false;
      return Object.values(value).every((v) => typeof v === "string");
    }),
});

const addToCartSchema = yup.object({
  action: yup.string().oneOf(["add_to_cart"]).required(),
  product: yup
    .object({
      id: yup.string().required(),
      name: yup.string().required(),
      quantity: yup.number().integer().positive().required(),
    })
    .required(),
});

export type SupportedChatMessagePayloadActions = {
  SubmitFormAction: {
    action: "submit_form";
    form_data: Record<string, string>;
  };
  AddToCartAction: yup.InferType<typeof addToCartSchema>;
};
